import { InlineSkeleton } from '@/components/loading/Skeleton'
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useDirector, type DirectorMode } from '@/hooks/useDirector';
import { DirectorMessage } from './DirectorMessage';
import { Badge, Button, IconButton, TextField } from '@/components/play';
import { Send, Wifi, WifiOff, RefreshCw, Sparkles, Trash2 } from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────

interface DirectorChatProps {
  eventId: string;
  mode?: DirectorMode;
  className?: string;
  placeholder?: string;
  welcomeMessage?: string;
  onWorkspaceUpdate?: () => void;
  initialPrompt?: string | null;
  onInitialPromptConsumed?: () => void;
  draftPrompt?: string | null;
}

// ─── Component ────────────────────────────────────────────────────

const PREVIEW_MUTATION_TOOLS = new Set([
  'create_stream',
  'start_stream',
  'stop_stream',
  'switch_featured_camera',
  'apply_destination_operations',
  'reset_destination_draft',
  'publish_destination',
  'connect_event_data_source',
  'set_component_live_state',
  'clear_component_live_state',
]);

export function DirectorChat({
  eventId,
  mode = 'design',
  className,
  placeholder = 'Describe what you want to stream…',
  welcomeMessage,
  onWorkspaceUpdate,
  initialPrompt,
  onInitialPromptConsumed,
  draftPrompt,
}: DirectorChatProps) {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastAutoPromptRef = useRef<string | null>(null);

  const {
    messages,
    isStreaming,
    connectionStatus,
    sendMessage,
    clearMessages,
    retryConnection,
  } = useDirector({
    eventId,
    mode,
    onToolResult: (toolResult) => {
      if (toolResult.success && PREVIEW_MUTATION_TOOLS.has(toolResult.name)) {
        onWorkspaceUpdate?.();
      }
    },
    onError: (error) => {
      console.error('[Director] Error:', error);
    },
  });

  // Keep the newest message in view: a smooth scroll shows where it came from, a jump under reduced motion.
  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    messagesEndRef.current?.scrollIntoView?.({ behavior: reduce ? 'auto' : 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!draftPrompt?.trim()) return;
    setInputValue(draftPrompt);
    inputRef.current?.focus();
  }, [draftPrompt]);

  const isConnected = connectionStatus === 'connected';
  const isConnecting = connectionStatus === 'connecting';
  const hasError = connectionStatus === 'error';

  useEffect(() => {
    const prompt = initialPrompt?.trim();
    if (!prompt || !isConnected || isStreaming) return;
    if (lastAutoPromptRef.current === prompt) return;

    lastAutoPromptRef.current = prompt;
    sendMessage(prompt);
    onInitialPromptConsumed?.();
  }, [initialPrompt, isConnected, isStreaming, onInitialPromptConsumed, sendMessage]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isStreaming) return;
    sendMessage(inputValue);
    setInputValue('');
  };

  return (
    <div
      className={cn(
        'flex h-full flex-col overflow-hidden rounded-2xl bg-neutral-1 text-neutral-10',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-neutral-4 px-4 py-3.5">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-neutral-3">
            <Sparkles className="w-4 h-4 text-neutral-10" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-neutral-10">Director</h3>
              <Badge>AI Producer</Badge>
            </div>
            <p className="mt-xxs text-xs text-neutral-7">
              {mode === 'design'
                ? 'Destination design'
                : mode === 'studio'
                  ? 'Live studio'
                  : 'Stream setup'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Connection status, as a Play badge: its glyph and words carry the state with the colour. */}
          {isConnected && <Badge tone="success" icon={<Wifi />}>Connected</Badge>}
          {isConnecting && <Badge tone="warning" icon={<RefreshCw />}>Connecting</Badge>}
          {hasError && <Badge tone="error" icon={<WifiOff />}>Not connected</Badge>}
          {connectionStatus === 'disconnected' && <Badge tone="quiet" icon={<WifiOff />}>Disconnected</Badge>}

          {/* Retry button */}
          {(hasError || connectionStatus === 'disconnected') && (
            <IconButton
              label="Reconnect"
              variant="ghost"
              size="compact"
              onClick={retryConnection}
            >
              <RefreshCw />
            </IconButton>
          )}

          {/* Clear button */}
          {messages.length > 0 && (
            <IconButton
              label="Clear conversation"
              variant="ghost"
              size="compact"
              onClick={clearMessages}
            >
              <Trash2 />
            </IconButton>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        {messages.length === 0 && !isStreaming && (
          <div className="flex flex-col items-center justify-center h-full px-6 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-3xl bg-neutral-3">
              <Sparkles className="w-8 h-8 text-neutral-10" />
            </div>
            <h4 className="text-lg font-semibold text-neutral-10 mb-2">
              {welcomeMessage || "Hi! I'm Director."}
            </h4>
            <p className="max-w-sm text-sm text-neutral-7">
              {mode === 'design'
                ? "Describe the destination you want. I'll compose it from trusted event components and connect the data it needs."
                : mode === 'studio'
                  ? "Direct the live destination. I can feature cameras and update supported component state for viewers in real time."
                  : "Tell me about your event and I'll help you set up cameras, streams, and invites."}
            </p>
            <div className="mt-6 flex flex-wrap gap-2 justify-center">
              {mode === 'design' ? (
                <>
                  <SuggestionChip onClick={() => setInputValue('Add a MultiView component using picture-in-picture and all online cameras')}>
                    MultiView
                  </SuggestionChip>
                  <SuggestionChip onClick={() => setInputValue('Add a compact ResultsView using the connected results source')}>
                    Results
                  </SuggestionChip>
                  <SuggestionChip onClick={() => setInputValue('Add a RouteMap using the connected tracking source')}>
                    Tracking
                  </SuggestionChip>
                  <SuggestionChip onClick={() => setInputValue('Connect the Sportraxs adapter for live timing and tracking')}>
                    Sportraxs
                  </SuggestionChip>
                </>
              ) : mode === 'studio' ? (
                <>
                  <SuggestionChip onClick={() => setInputValue('Feature the lead camera')}>
                    Feature camera
                  </SuggestionChip>
                  <SuggestionChip onClick={() => setInputValue('Show the sponsor message on the destination')}>
                    Sponsor message
                  </SuggestionChip>
                  <SuggestionChip onClick={() => setInputValue('Inspect the connected timing provider status')}>
                    Provider status
                  </SuggestionChip>
                </>
              ) : (
                <>
                  <SuggestionChip onClick={() => setInputValue('Set up 4 cameras along a trail race course')}>
                    Trail race
                  </SuggestionChip>
                  <SuggestionChip onClick={() => setInputValue('Create a marathon with 3 cameras on the course')}>
                    Marathon
                  </SuggestionChip>
                  <SuggestionChip onClick={() => setInputValue('Generate QR codes for camera operators')}>
                    Camera invites
                  </SuggestionChip>
                </>
              )}
            </div>
          </div>
        )}

        {messages.map((message) => (
          <DirectorMessage key={message.id} message={message} />
        ))}

        {/* Thinking indicator */}
        {isStreaming && messages.length > 0 && !messages[messages.length - 1]?.isStreaming && (
          <div className="px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-neutral-7">
              <InlineSkeleton label="Director is working" className="w-4 h-4" />
              <span>Director is thinking…</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="border-t border-neutral-4 p-4">
        <div className="flex items-start gap-2">
          <TextField
            ref={inputRef}
            label="Message to the producer"
            hideLabel
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={placeholder}
            disabled={!isConnected || isStreaming}
            className="flex-1"
          />
          <IconButton
            type="submit"
            label="Send message"
            variant="primary"
            loading={isStreaming}
            disabled={!inputValue.trim() || !isConnected}
          >
            <Send />
          </IconButton>
        </div>
      </form>
    </div>
  );
}

// ─── Suggestion Chip ──────────────────────────────────────────────

function SuggestionChip({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <Button variant="secondary" size="compact" onClick={onClick}>
      {children}
    </Button>
  );
}

export default DirectorChat;
